import { FormInput, SubmitButton } from '@/components/form'
import { CUSTOMER_TYPE } from '@/constants/account'
import { ROUTES } from '@/constants/routes'
import { useUser } from '@/hooks/useUser'
import { organizationsService } from '@/services/organizations'
import { handleApiError } from '@/utils/errors'
import { useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/lib/query-keys'
import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

function getOrganizationId(user: {
  organization_quote?: { organization_id: string } | null
  subscriptions?: Array<{ organization_id?: string }>
}): string | null {
  return (
    user.organization_quote?.organization_id ?? user.subscriptions?.[0]?.organization_id ?? null
  )
}

export function AddMemberPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: user, isLoading: userLoading } = useUser()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [rppsNumber, setRppsNumber] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [genericError, setGenericError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const organizationId = user ? getOrganizationId(user) : null
  const isOrganization = user?.customer_type === CUSTOMER_TYPE.ORGANIZATION

  if (userLoading || !user) {
    return (
      <div className="bg-background px-4 py-8">
        <div className="mx-auto max-w-[605px]">
          <div className="bg-placeholder h-8 w-48 rounded" />
        </div>
      </div>
    )
  }

  if (!isOrganization || !organizationId) {
    navigate(ROUTES.SEATS, { replace: true })
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setGenericError(null)
    setFieldErrors({})
    setIsSubmitting(true)
    try {
      await organizationsService.inviteMember(organizationId, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        rpps_number: rppsNumber.trim(),
      })
      await queryClient.invalidateQueries({
        queryKey: ['organizationMembers', organizationId],
      })
      await queryClient.invalidateQueries({ queryKey: queryKeys.users.me })
      navigate(ROUTES.SEATS, { replace: true })
    } catch (err) {
      const apiError = handleApiError(err)
      const errors: Record<string, string> = {}
      if (apiError.errors && typeof apiError.errors === 'object') {
        for (const [key, messages] of Object.entries(apiError.errors)) {
          if (Array.isArray(messages) && messages[0]) errors[key] = messages[0]
        }
      }
      setFieldErrors(errors)
      const status =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { status?: number } }).response?.status
          : undefined
      if (status === 500 || status === 502) {
        setGenericError(t('seats.errors.inviteFailed'))
      } else if (Object.keys(errors).length === 0) {
        setGenericError(apiError.message)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="bg-background px-4 py-8">
      <div className="mx-auto max-w-[605px]">
        <Link
          to={ROUTES.SEATS}
          className="text-foreground hover:text-primary mb-6 inline-flex items-center gap-2 text-sm transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="text-foreground text-xl font-semibold">
            {t('seats.addMemberPageTitle')}
          </span>
        </Link>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {genericError && (
            <div
              className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-600"
              role="alert"
            >
              {genericError}
            </div>
          )}

          <FormInput
            id="last_name"
            label={t('seats.form.lastName')}
            required
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            placeholder=""
            autoComplete="family-name"
            disabled={isSubmitting}
          />
          {fieldErrors.last_name && (
            <p className="text-destructive -mt-2 text-sm">{fieldErrors.last_name}</p>
          )}

          <FormInput
            id="first_name"
            label={t('seats.form.firstName')}
            required
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            placeholder=""
            autoComplete="given-name"
            disabled={isSubmitting}
          />
          {fieldErrors.first_name && (
            <p className="text-destructive -mt-2 text-sm">{fieldErrors.first_name}</p>
          )}

          <FormInput
            id="email"
            label={t('seats.form.email')}
            type="email"
            required
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder=""
            autoComplete="email"
            disabled={isSubmitting}
          />
          {fieldErrors.email && (
            <p className="text-destructive -mt-2 text-sm">{fieldErrors.email}</p>
          )}

          <FormInput
            id="rpps_number"
            label={t('seats.form.rpps')}
            value={rppsNumber}
            onChange={e => setRppsNumber(e.target.value)}
            placeholder=""
            autoComplete="off"
            disabled={isSubmitting}
          />
          {fieldErrors.rpps_number && (
            <p className="text-destructive -mt-2 text-sm">{fieldErrors.rpps_number}</p>
          )}

          <SubmitButton
            className="self-start"
            showIcon={false}
            isLoading={isSubmitting}
            loadingLabel={t('seats.form.submit')}
          >
            {t('seats.form.submit')}
          </SubmitButton>
        </form>
      </div>
    </div>
  )
}
